/**
 * Vínculo indicador ↔ instrumento
 * (`/api/indicador-instrumentos`).
 *
 * Es la ÚNICA forma de saber qué instrumentos miden un indicador: no existe una
 * columna `instrumentos.id_indicador`. La relación es M:N en ambos sentidos, que
 * es lo que permite reutilizar un formulario del repositorio sin duplicarlo.
 *
 * Capa de servicios: una función por operación del API. No lanza nunca —cada
 * función devuelve un valor neutro ante el fallo—, así que el llamador debe
 * comprobar el resultado, no envolver en try/catch.
 */

import type IndicadorInstrumento from '@/interfaces/IndicadorInstrumento'
import { http } from '@/services/http'

/**
 * Consulta indicador instrumento · petición `GET`.
 *
 * @returns IndicadorInstrumento[]
 */
export async function getIndicadorInstrumentosService(url: string): Promise<IndicadorInstrumento[]> {
  const result = await http.get<any>(url)
  if (!result.ok) throw new Error('Error al listar relaciones')
  const d = result.data
  return Array.isArray(d) ? d : (d?.data ?? [])
}

/**
 * Consulta indicador instrumento · petición `GET`.
 *
 * @returns IndicadorInstrumento
 */
export async function getIndicadorInstrumentoByIdService(url: string, id: number): Promise<IndicadorInstrumento> {
  const result = await http.get<IndicadorInstrumento>(`${url}/${id}`)
  if (!result.ok) throw new Error(`Error al obtener relación con id ${id}`)
  return result.data!
}

/**
 * Crea indicador instrumento · petición `POST`.
 *
 * @returns IndicadorInstrumento
 */
export async function crearIndicadorInstrumentoService(
  url: string,
  payload: Omit<IndicadorInstrumento, 'id'>
): Promise<IndicadorInstrumento> {
  const result = await http.post<IndicadorInstrumento>(url, payload)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? 'Error al crear relación'
    throw new Error(msg)
  }
  return result.data!
}

/**
 * Elimina indicador instrumento · petición `DELETE`.
 *
 * @returns boolean
 */
export async function eliminarIndicadorInstrumentoService(url: string, id: number): Promise<boolean> {
  const result = await http.delete(`${url}/${id}`)
  if (!result.ok) {
    const msg = Object.values(result.errors ?? {}).flat()[0] ?? `Error al eliminar relación con id ${id}`
    throw new Error(msg)
  }
  return true
}
